import { listJsonPointerPaths } from './json-pointer-suggestions';
import {
  jsonPointerGet,
  parseToolCallParamBindingRef,
  type ParsedToolCallParamBindingRef,
} from '@cotera/utilities';
import type { ToolCallUpstreamSample } from '../../../hooks/use-tool-call-upstream-samples';

/** Decode an RFC 6901 pointer token (`~1` -> `/`, `~0` -> `~`). */
export function decodeToken(token: string): string {
  return token.replace(/~1/g, '/').replace(/~0/g, '~');
}

/** `/pull_request/title` -> `pull_request.title` (for pill labels). */
export function dottedPathFromRef(ref: string): string {
  try {
    const { sourceName, pointer } = parseToolCallParamBindingRef(ref);
    const segments = pointer
      .split('/')
      .filter((segment) => segment !== '')
      .map(decodeToken);
    // Root pointer (empty path) — show the source name so pills / chips
    // aren't blank when the upstream step has no typed output fields.
    if (segments.length === 0) {
      return sourceName ?? ref;
    }
    return segments.join('.');
  } catch {
    return ref;
  }
}

/**
 * A ref as a human label with array indices collapsed to `[]`:
 * `/groups/0/items/0/order_id` -> `groups[].items[].order_id`.
 *
 * The picker builds pointers from a sample, so an array shows up as index `0`
 * (`listJsonPointerPaths`). Rendered verbatim that reads as "the first group's
 * first item" when the ref means "a field inside the list". Collapsing to `[]`
 * says that instead.
 *
 * Display only. The stored ref keeps the real pointer, and {@link
 * dottedPathFromRef} stays the literal form — it also derives a for-each's
 * `itemsPath` (see `for-each-items-path`), where `[]` would be wrong. The index
 * test is a string heuristic, so a numeric *object* key (rare in tool/agent
 * output) would also collapse; harmless for a label.
 */
export function displayDottedPathFromRef(ref: string): string {
  try {
    const { sourceName, pointer } = parseToolCallParamBindingRef(ref);
    const segments = pointer
      .split('/')
      .filter((segment) => segment !== '')
      .map(decodeToken);
    if (segments.length === 0) {
      return sourceName ?? ref;
    }
    const parts: string[] = [];
    for (const segment of segments) {
      if (/^\d+$/.test(segment)) {
        // An array index collapses onto the list it indexes, as `[]`.
        if (parts.length > 0) {
          parts[parts.length - 1] += '[]';
        } else {
          parts.push('[]');
        }
      } else {
        parts.push(segment);
      }
    }
    return parts.join('.');
  } catch {
    return ref;
  }
}

export function displayLabelForRef(
  ref: string,
  sourceLabels: ReadonlyMap<string, string>
): string {
  try {
    const { sourceName, pointer } = parseToolCallParamBindingRef(ref);
    if (sourceName !== undefined && pointer === '') {
      return sourceLabels.get(sourceName) ?? sourceName;
    }
  } catch {
    return ref;
  }
  return displayDottedPathFromRef(ref);
}

/**
 * What a variable pill shows for a saved ref.
 *
 * A saved token carries the source's stable id, not its name, so a reload has
 * nothing but a uuid to render unless the current upstream sources are asked —
 * which is how a pill reading "Enrich lead" when it was inserted came back as
 * `a3f1…` on the next open. Resolved live rather than from the label stored on
 * the pill so a renamed node reads correctly too; the stored label only stands
 * in when the source is no longer upstream and nothing can name it any more.
 */
export function pillLabelForRef(params: {
  readonly ref: string;
  readonly sourceLabels: ReadonlyMap<string, string>;
  readonly savedLabel?: string | null;
}): string {
  const { ref, sourceLabels, savedLabel } = params;
  const fallback =
    typeof savedLabel === 'string' && savedLabel !== '' ? savedLabel : null;
  const parsed = ((): ParsedToolCallParamBindingRef | null => {
    try {
      return parseToolCallParamBindingRef(ref);
    } catch {
      return null;
    }
  })();
  if (parsed === null) {
    return fallback ?? ref;
  }
  // A field inside a source names itself; the dotted path is what the picker
  // showed and is the shorter, more specific of the two.
  if (parsed.pointer !== '' || parsed.sourceName === undefined) {
    return displayDottedPathFromRef(ref);
  }
  return sourceLabels.get(parsed.sourceName) ?? fallback ?? parsed.sourceName;
}

/**
 * The friendly name of the source a ref reads from, or null when the ref names
 * no source (a legacy bare JSON Pointer from a single-upstream workflow).
 */
export function sourceLabelOfRef(
  ref: string,
  sourceLabels: ReadonlyMap<string, string>
): string | null {
  try {
    const { sourceName } = parseToolCallParamBindingRef(ref);
    return sourceName === undefined
      ? null
      : sourceLabels.get(sourceName) ?? sourceName;
  } catch {
    return null;
  }
}

/**
 * Source id to friendly name, for resolving a saved ref back to something a
 * person named. Field-level entries as well as group ones, because a unioned
 * group speaks for several sources and only its fields still say which.
 */
export function sourceLabelsFromGroups(
  sources: readonly UpstreamSourceGroup[]
): ReadonlyMap<string, string> {
  return new Map(
    sources.flatMap((group) => [
      [group.sourceName, group.label] as const,
      ...group.fields.map((field) => [field.sourceName, field.label] as const),
    ])
  );
}

export function truncate(text: string, max = 40): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/**
 * A short, human hint for a sample value — scalars show the value, containers
 * read as containers rather than dumping raw JSON.
 */
/**
 * The field's TYPE for the right-hand hint, inferred from its sample value:
 * `string`, `number`, `array`, and so on. Shown instead of the sample value so a
 * row reads as "pick a string here" rather than showing whatever the last run
 * happened to hold — an empty-string sample rendered as `""`, which read like a
 * bug, and a number as a bare `0`, which read like a value worth picking. The
 * type is the stable thing a field offers; the value is incidental. `undefined`
 * (no sample, e.g. a not-yet-run upstream) has no knowable type, so `any`.
 */
export function describeType(value: unknown): { text: string; mono: boolean } {
  if (value === undefined) {
    return { text: 'any', mono: false };
  }
  if (value === null) {
    return { text: 'null', mono: false };
  }
  if (Array.isArray(value)) {
    return { text: 'array', mono: false };
  }
  if (typeof value === 'object') {
    return { text: 'object', mono: false };
  }
  // string | number | boolean all read correctly as `typeof`.
  return { text: typeof value, mono: false };
}

export type UpstreamFieldOption = {
  readonly ref: string;
  readonly sourceName: string;
  readonly label: string;
  readonly leaf: string;
  readonly depth: number;
  /** Ancestor distance of the source node: 1 = direct predecessor, 2+ = earlier. */
  readonly sourceDepth: number;
  readonly value: unknown;
  readonly searchText: string;
};

export type UpstreamSourceGroup = {
  readonly sourceName: string;
  /** Friendly heading (e.g. an agent's title). */
  readonly label: string;
  /** Ancestor distance of the source node: 1 = direct predecessor, 2+ = earlier. */
  readonly sourceDepth: number;
  readonly fields: readonly UpstreamFieldOption[];
};

/**
 * Groups every upstream output field by its source node, resolving each into a
 * tree-friendly option (leaf name + depth + sample value).
 */
/**
 * Pickable JSON Pointers into ONE value, as a single named group.
 *
 * For the editors whose paths are bare pointers into a single value rather than
 * `Node/pointer` refs into any ancestor — a bucket's accumulate path, a group-by's
 * key. Those all resolve against one step's output at run time, so offering a
 * choice of sources would produce config the runner cannot honour.
 */
export function singleSamplePointerSources(params: {
  readonly sample: unknown;
  readonly sourceName: string;
  readonly label: string;
  /**
   * Offer the value as a whole — a bare empty pointer, which these editors read
   * as "the whole value" — when it has no fields to drill into. Without it a
   * scalar or opaque sample (an agent's plain-text output, an unresolved
   * `{{node}}` placeholder) yields no group, so the picker's bolt hides
   * entirely. This is the same whole-step fallback `buildUpstreamSources` gives
   * node-scoped pickers via {@link pointerSuggestionsForSample}, which is why a
   * bucket's field shows its bolt where an equivalent single-sample field did
   * not.
   */
  readonly includeWholeValue?: boolean;
}): UpstreamSourceGroup[] {
  if (params.sample === undefined) {
    return [];
  }
  const sources = buildUpstreamSources([
    {
      sourceName: params.sourceName,
      label: params.label,
      sourceDepth: 1,
      sample: params.sample,
      pointerSuggestions: listJsonPointerPaths(params.sample),
    },
  ]);
  if (sources.length > 0 || params.includeWholeValue !== true) {
    return sources;
  }
  // No drillable paths: offer the whole value so the bolt still appears. Built
  // directly rather than through `buildUpstreamSources`, whose field builder
  // parses each ref and an empty pointer is not a parseable ref. The value is
  // left off the row so an opaque `{{node}}` placeholder is not shown as the
  // hint (as `datasetColumnSections` does for the same reason).
  return [
    {
      sourceName: params.sourceName,
      label: params.label,
      sourceDepth: 1,
      fields: [
        {
          ref: '',
          sourceName: params.sourceName,
          label: params.label,
          leaf: params.label,
          depth: 0,
          sourceDepth: 1,
          value: undefined,
          searchText: params.label,
        },
      ],
    },
  ];
}

export function buildUpstreamSources(
  upstreamSamples: readonly ToolCallUpstreamSample[]
): UpstreamSourceGroup[] {
  return upstreamSamples
    .map((source) => ({
      sourceName: source.sourceName,
      label: source.label,
      sourceDepth: source.sourceDepth,
      fields: source.pointerSuggestions.map((ref) => {
        const { pointer } = parseToolCallParamBindingRef(ref);
        const segments = pointer
          .split('/')
          .filter((segment) => segment !== '')
          .map(decodeToken);
        const leaf = segments[segments.length - 1] ?? source.label;
        return {
          ref,
          sourceName: source.sourceName,
          label: source.label,
          leaf,
          depth: Math.max(0, segments.length - 1),
          sourceDepth: source.sourceDepth,
          value: jsonPointerGet(source.sample, pointer),
          searchText: `${source.label} ${source.sourceName} ${segments.join(
            ' '
          )}`,
        };
      }),
    }))
    .filter((group) => group.fields.length > 0);
}

/**
 * When a node has multiple predecessors, collapse field lists into one group
 * keyed by JSON Pointer (set-union of paths across upstream samples) — same
 * idea as the filter editor's pointer suggestions. Prefer an array-valued
 * sample when the same path appears on more than one upstream so for-each can
 * still pick a fan-out array.
 */
export function unionUpstreamSources(
  sources: readonly UpstreamSourceGroup[]
): readonly UpstreamSourceGroup[] {
  if (sources.length <= 1) {
    return sources;
  }

  type Acc = {
    field: UpstreamFieldOption;
    sourceLabels: string[];
  };
  const byPointer = new Map<string, Acc>();

  for (const group of sources) {
    for (const field of group.fields) {
      let pointer: string;
      try {
        pointer = parseToolCallParamBindingRef(field.ref).pointer;
      } catch {
        pointer = field.ref;
      }
      const existing = byPointer.get(pointer);
      if (existing === undefined) {
        byPointer.set(pointer, {
          field,
          sourceLabels: [group.label],
        });
        continue;
      }
      if (!existing.sourceLabels.includes(group.label)) {
        existing.sourceLabels.push(group.label);
      }
      if (Array.isArray(field.value) && !Array.isArray(existing.field.value)) {
        existing.field = field;
      }
    }
  }

  const fields = [...byPointer.values()].map(({ field, sourceLabels }) => ({
    ...field,
    searchText: `${sourceLabels.join(' ')} ${field.searchText}`,
  }));

  return [
    {
      sourceName: '__union__',
      label: 'Previous steps',
      sourceDepth: 1,
      fields,
    },
  ];
}
